import { useEffect, useState } from "react";
import { Upload, FileText, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { GlassCard, PageHeader, EmptyState } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const TYPES = ["aadhaar", "pan", "resume", "nda", "other"];

export default function Documents() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get(`/documents/employee/${user.id}`);
            setItems(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    return (
        <div>
            <PageHeader
                title="Documents"
                subtitle="Upload identity proofs and access auto-generated letters."
                testId="documents-header"
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full" data-testid="upload-doc-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
                            <UploadForm onUploaded={() => { setOpen(false); load(); }} userId={user.id} />
                        </DialogContent>
                    </Dialog>
                }
            />

            {loading ? (
                <div className="text-center py-8 text-sm text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <GlassCard><EmptyState icon={FileText} title="No documents" description="Upload your identity proofs and resume." /></GlassCard>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((d) => (
                        <GlassCard key={d.id} className="p-5 flex items-center gap-3 glass-hover" data-testid={`doc-${d.id}`}>
                            <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><FileText className="w-5 h-5" /></div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{d.name}</div>
                                <div className="text-xs text-muted-foreground capitalize">{d.doc_type.replace("_", " ")}</div>
                                <div className="text-[11px] text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</div>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}
        </div>
    );
}

function UploadForm({ onUploaded, userId }) {
    const [form, setForm] = useState({ name: "", doc_type: "resume", file_url: "", content: "" });
    const [loading, setLoading] = useState(false);

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            setForm({ ...form, name: f.name, content: String(reader.result).split(",")[1] || "" });
        };
        reader.readAsDataURL(f);
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/documents/upload", { ...form, employee_id: userId });
            toast.success("Uploaded");
            onUploaded();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-3">
            <div><Label>File</Label><Input required type="file" onChange={handleFile} className="mt-1" data-testid="doc-file" /></div>
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Type</Label>
                <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid="doc-type">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="doc-submit">{loading ? "Uploading…" : "Upload"}</Button>
        </form>
    );
}
